// src/components/CallToAction.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const CallToAction = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  return (
    <CTASection ref={ref}>
      <Container>
        <CTATitle
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          Ready for strategic marketing that delivers results?
        </CTATitle>
        
        <CTADescription
          as={motion.p}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Whether you're scaling a business, optimizing performance, or transitioning to in-house marketing operations, I'm here to help you build systems that drive tangible business outcomes.
        </CTADescription>
        
        <ButtonContainer
          as={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <CTAButton 
  href="https://calendar.app.google/JNzzrs67cJGNb3Vq8" 
  target="_blank"
>
  Schedule a Strategy Consultation
  <ButtonIcon>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </ButtonIcon>
</CTAButton>
          
          <PhoneButton href="tel:+2349020489434">
            <ButtonIcon>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
            </ButtonIcon>
            +234 (902) 048-9434
          </PhoneButton>
        </ButtonContainer>
        
        <BackgroundCircles>
          <Circle1 />
          <Circle2 />
          <Circle3 />
        </BackgroundCircles>
      </Container>
    </CTASection>
  );
};

const CTASection = styled.section`
  padding: 8rem 0;
  background: linear-gradient(120deg, #4f46e5, #7c3aed 40%, #db2777 100%);
  background-size: 200% 200%;
  animation: gradientShift 12s ease infinite alternate;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(255,255,255,0.12), transparent 30%),
      radial-gradient(circle at 80% 70%, rgba(255,255,255,0.08), transparent 30%);
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const CTATitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  max-width: 700px;
  margin: 0 auto 2.5rem;
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.125rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  color: #6366f1;
  font-weight: 700;
  font-size: 1.125rem;
  padding: 1rem 2rem;
  border-radius: 9999px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  transition: all ${({ theme }) => theme.transitions.default};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }
`;

const PhoneButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
  padding: 1rem 2rem;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
  }
`;

const ButtonIcon = styled.span`
  display: flex;
  align-items: center;
`;

const BackgroundCircles = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
`;

const Circle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
`;

const Circle1 = styled(Circle)`
  width: 30vw;
  height: 30vw;
  top: -15vw;
  left: -10vw;
`;

const Circle2 = styled(Circle)`
  width: 20vw;
  height: 20vw;
  bottom: -5vw;
  right: -5vw;
`;

const Circle3 = styled(Circle)`
  width: 15vw;
  height: 15vw;
  bottom: 20%;
  left: 20%;
`;

export default CallToAction;